import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

class WebSocketService {
  constructor() {
    this.client = null;
    this.connected = false;
    this.subscriptions = new Map();
  }

  connect(token) {
    return new Promise((resolve, reject) => {
      const socketUrl = import.meta.env.VITE_WS_URL || 'http://localhost:8080/ws';

      this.client = new Client({
        webSocketFactory: () => new SockJS(socketUrl),
        connectHeaders: {
          Authorization: `Bearer ${token}`,
        },
        debug: (str) => {
          console.log('STOMP:', str);
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
      });

      this.client.onConnect = () => {
        console.log('WebSocket connected');
        this.connected = true;
        resolve();
      };

      this.client.onStompError = (frame) => {
        console.error('WebSocket error:', frame);
        this.connected = false;
        reject(frame);
      };

      this.client.onDisconnect = () => {
        console.log('WebSocket disconnected');
        this.connected = false;
      };

      this.client.activate();
    });
  }

  disconnect() {
    if (this.client) {
      this.client.deactivate();
      this.subscriptions.clear();
      this.connected = false;
    }
  }

  subscribe(destination, callback) {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return null;
    }

    const subscription = this.client.subscribe(destination, (message) => {
      try {
        const data = JSON.parse(message.body);
        callback(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });

    this.subscriptions.set(destination, subscription);
    return subscription;
  }

  unsubscribe(destination) {
    const subscription = this.subscriptions.get(destination);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(destination);
    }
  }

  // Subscribe to user-specific metrics updates
  subscribeToMetrics(userId, callback) {
    return this.subscribe(`/user/${userId}/queue/metrics`, callback);
  }

  // Subscribe to health alerts
  subscribeToHealthAlerts(userId, callback) {
    return this.subscribe(`/user/${userId}/queue/health-alerts`, callback);
  }

  // Subscribe to notifications
  subscribeToNotifications(userId, callback) {
    return this.subscribe(`/user/${userId}/queue/notifications`, callback);
  }

  // Subscribe to revenue updates
  subscribeToRevenue(userId, callback) {
    return this.subscribe(`/user/${userId}/queue/revenue`, callback);
  }

  // Subscribe to anomaly alerts
  subscribeToAnomalies(userId, callback) {
    return this.subscribe(`/user/${userId}/queue/anomalies`, callback);
  }

  // Subscribe to public metrics topic (broadcast)
  subscribeToPublicMetrics(callback) {
    return this.subscribe('/topic/metrics', callback);
  }

  isConnected() {
    return this.connected;
  }
}

export default new WebSocketService();
